import { List, Map } from 'immutable';

import { setCategoryErrors, setItemSpecificsErrors, setPhotosErrors, setVariationsErrors, updateProductVariationsErrors } from '../product/errors';
import { makeOperation, makeOperationValue } from '../inventory';
import { shapeEbayDetailsForAPI } from '../product/shapeAttributes';
import { shapeVariationsForAPI } from '../variations/shapeForAPI';
import { isValidNumber } from '../number';
import { reduceErrors } from '../errors';
import { areEqual } from '../iterable/areEqual';
import { getSize } from '../iterable/getSize';

import { AMOUNT, EMPTY_VARIATIONS, ERROR, OPERATIONS, SELLING_METHOD, VALUE } from '../../constants/product';
import { COMBINATIONS, INFLUENCE, RETAIN, VARIANTS } from '../../constants/variations';
import { ERRORS, MAX_QUANTITY } from '../../constants/validations';
import { EBAY, ETSY, FAIRE } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function editVariations({ product: source, shopData, taxonomyData, value }) {
  let product = source;
  const operations = [];
  const channel = product.get('channel');
  let operation = Map({ type: OPERATIONS[channel].VARIATIONS });
  let confirmation;

  function removeVariationsErrors(errors = EMPTY.MAP) {
    function filterVariationsErrors(_, key) {
      return !/^variations/.test(key);
    }

    return errors.filter(filterVariationsErrors);
  }

  function findVisible(combination) {
    return combination.get(ATTRIBUTE.VISIBILITY);
  }

  function reduceCombinationsToQuantity(result, combination, combinationIndex, combinations) {
    const maximum = MAX_QUANTITY[channel] - 1;

    if (result >= maximum) return maximum;

    if (!combination.get(ATTRIBUTE.VISIBILITY)) {
      return result
        ? result
        : combinationIndex === getSize(combinations) - 1
          ? Math.min(parseInt(combinations.getIn([0, ATTRIBUTE.QUANTITY])) || 0, maximum) || 1
          : result;
    }

    return Math.min((parseInt(combination.get(ATTRIBUTE.QUANTITY)) || 0) + result, maximum);
  }

  if (getSize(value.get(VARIANTS)) === 0 && getSize(product.getIn([FORM.VARIATIONS, VARIANTS]))) {
    if (product.has(ERROR)) {
      product = product.update(ERROR, removeVariationsErrors);
    }

    switch (channel) {
      case EBAY: {
        product = setVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData, taxonomyData });
        product = setCategoryErrors({ product, shopData, taxonomyData });
        product = setItemSpecificsErrors({ product, shopData, taxonomyData });

        if (getSize(product.getIn([FORM.PHOTOS, VALUE])) === 0) {
          product = setPhotosErrors({ product });
        }

        if (
          source.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, ERROR]) === ERRORS.VARIATIONS.NOT_ALLOWED &&
          !product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, ERROR])
        ) {
          operations.push(
            Map({
              type: OPERATIONS[EBAY].ITEM_CATEGORY,
              value: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]),
            })
          );
        }

        break;
      }

      case ETSY: {
        const combinations = product.getIn([FORM.VARIATIONS, COMBINATIONS]);
        const influence = product.getIn([FORM.VARIATIONS, INFLUENCE]);

        const price = getSize(influence.get(ATTRIBUTE.PRICE)) > 0
          ? getSize(combinations)
            ? (combinations.find(findVisible) || combinations.last()).get(ATTRIBUTE.PRICE)
            : product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE])
          : product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]);

        const quantity = getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0
          ? getSize(combinations)
            ? String(combinations.reduce(reduceCombinationsToQuantity, 0))
            : product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE])
          : product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]);

        const sku = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);

        product = updateProductVariationsErrors({
          product: product
            .setIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE], price || AMOUNT.ZERO_CENT)
            .setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], quantity || AMOUNT.ZERO)
            .setIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], sku || '')
            .set(FORM.VARIATIONS, value),
          shopData,
        });

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR])) {
          const priceOperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: ATTRIBUTE.PRICE,
          });

          if (priceOperationValue) {
            const priceOperation = makeOperation({
              channel,
              type: ATTRIBUTE.PRICE,
              value: List([priceOperationValue]),
            });

            if (priceOperation) {
              operations.push(priceOperation);
            }
          }
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR])) {
          const quantityOperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: ATTRIBUTE.QUANTITY,
          });

          if (quantityOperationValue) {
            const quantityOperation = makeOperation({
              channel,
              type: ATTRIBUTE.QUANTITY,
              value: List([quantityOperationValue]),
            });

            if (quantityOperation) {
              operations.push(quantityOperation);
            }
          }
        }

        if (!product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR])) {
          const SKUoperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: ATTRIBUTE.SKU,
          });

          if (SKUoperationValue) {
            const SKUoperation = makeOperation({
              channel,
              type: ATTRIBUTE.SKU,
              value: List([SKUoperationValue]),
            });

            if (SKUoperation) {
              operations.push(SKUoperation);
            }
          }
        }

        break;
      }

      case FAIRE: {
        product = setVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });

        if (
          source.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]) === SELLING_METHOD.SIZE &&
          product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]) !== SELLING_METHOD.SIZE
        ) {
          const minimumOrderQuantityError = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, ERROR]);
          const minimumOrderQuantityValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]);

          operations.push(
            Map({ type: OPERATIONS[FAIRE].CASE_SIZE, value: null }),
            Map({ type: OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY, value: null })
          );

          if (!minimumOrderQuantityError) {
            operations.push(
              Map({
                type: OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY,
                value: isValidNumber(minimumOrderQuantityValue)
                  ? parseInt(minimumOrderQuantityValue)
                  : null,
              })
            );
          }
        }

        break;
      }

      default: {
        product = updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
        break;
      }
    }

    switch (channel) {
      case ETSY: {
        const operationData = shapeVariationsForAPI({ product, toImmutable: true });
        operation = operation.set(VALUE, operationData.get(VALUE));
        break;
      }

      default: {
        operation = operation.set(VALUE, EMPTY_VARIATIONS[channel]);
        break;
      }
    }

    operations.unshift(operation);
  } else {
    switch (channel) {
      case EBAY: {
        product = setVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
        product = setItemSpecificsErrors({ product, shopData, taxonomyData });

        if (
          getSize(value.get(INFLUENCE)) > 0 ||
          getSize(source.getIn([FORM.VARIATIONS, INFLUENCE])) > 0
        ) {
          const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);

          if (
            !itemSpecifics.has(ERROR) && (
              source.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR]) ||
              !areEqual(
                itemSpecifics.get(VALUE),
                source.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE]),
              )
            )
          ) {
            operations.push(
              Map({
                type: OPERATIONS[EBAY].ITEM_SPECIFICS.SET,
                value: shapeEbayDetailsForAPI({ details: itemSpecifics }),
              })
            );
          }
        }

        if (getSize(product.getIn([FORM.PHOTOS, VALUE])) === 0) {
          product = setPhotosErrors({ product });
        }

        break;
      }

      case ETSY: {
        if (getSize(product.getIn([FORM.VARIATIONS, VARIANTS])) > 0) {
          const nextInfluence = value.get(INFLUENCE);
          const currentInfluence = product.getIn([FORM.VARIATIONS, INFLUENCE]);

          if (
            getSize(nextInfluence.get(ATTRIBUTE.PRICE)) === 0 &&
            getSize(currentInfluence.get(ATTRIBUTE.PRICE)) > 0
          ) {
            const price = value.getIn([COMBINATIONS, 0, ATTRIBUTE.PRICE]);
            product = product.setIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE], price);
          }

          if (
            getSize(nextInfluence.get(ATTRIBUTE.QUANTITY)) === 0 &&
            getSize(currentInfluence.get(ATTRIBUTE.QUANTITY)) > 0
          ) {
            const quantity = value.getIn([COMBINATIONS, 0, ATTRIBUTE.QUANTITY]);
            product = product.setIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], quantity);
          }

          if (
            getSize(nextInfluence.get(ATTRIBUTE.SKU)) === 0 &&
            getSize(currentInfluence.get(ATTRIBUTE.SKU)) > 0
          ) {
            const sku = value.getIn([COMBINATIONS, 0, ATTRIBUTE.SKU]);
            product = product.setIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], sku);
          }
        }

        product = updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
        break;
      }

      case FAIRE: {
        product = setVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });

        if (
          source.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]) === SELLING_METHOD.SIZE &&
          product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]) !== SELLING_METHOD.SIZE
        ) {
          const minimumOrderQuantityError = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, ERROR]);
          const minimumOrderQuantityValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]);

          operations.push(
            Map({ type: OPERATIONS[FAIRE].CASE_SIZE, value: null }),
            Map({ type: OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY, value: null })
          );

          if (!minimumOrderQuantityError) {
            operations.push(
              Map({
                type: OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY,
                value: isValidNumber(minimumOrderQuantityValue)
                  ? parseInt(minimumOrderQuantityValue)
                  : null,
              })
            );
          }
        }

        if (
          value.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]) !==
          source.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING])
        ) {
          const internationalPricing = value.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]);
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.INTERNATIONAL_PRICING, VALUE], internationalPricing);
        }

        if (
          value.getIn([INFLUENCE, ATTRIBUTE.CONTINUE_SELLING]) !==
          source.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.CONTINUE_SELLING])
        ) {
          const continueSelling = value.getIn([INFLUENCE, ATTRIBUTE.CONTINUE_SELLING]);
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.CONTINUE_SELLING, VALUE], continueSelling);
          operations.push(Map({ type: OPERATIONS[FAIRE].CONTINUE_SELLING, value: continueSelling }));
        }

        if (
          value.getIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE]) !==
          source.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.KEEP_ACTIVE])
        ) {
          const keepActive = value.getIn([INFLUENCE, ATTRIBUTE.KEEP_ACTIVE]);
          product = product.setIn([FORM.INVENTORY, ATTRIBUTE.KEEP_ACTIVE, VALUE], keepActive);
          operations.push(Map({ type: OPERATIONS[FAIRE].KEEP_ACTIVE, value: keepActive }));
        }

        if (
          value.get(RETAIN) === source.getIn([FORM.VARIATIONS, RETAIN]) &&
          value.get(VARIANTS) === source.getIn([FORM.VARIATIONS, VARIANTS]) &&
          value.get(COMBINATIONS) === source.getIn([FORM.VARIATIONS, COMBINATIONS])
        ) {
          return { product, operations };
        }

        break;
      }

      default: {
        product = updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
        break;
      }
    }

    if (!reduceErrors(product.getIn([FORM.VARIATIONS, ERROR]))) {
      const operationData = shapeVariationsForAPI({ product, toImmutable: true });

      operation = operation.set(VALUE, operationData.get(VALUE));

      if (operationData.has('templateId')) {
        operation = operation.set('templateId', operationData.get('templateId'));
      }

      if (operationData.has('shopifyMeta')) {
        operation = operation.set('shopifyMeta', operationData.get('shopifyMeta'));
      }

      operations.unshift(operation);
    }
  }

  return { confirmation, product, operations };
}
